import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, WHATSAPP_URL } from "../lib/api";
import { ArrowLeft, Phone } from "lucide-react";
import SEO from "../components/SEO";

export default function BlogDetail() {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    api
      .get(`/blogs/${slug}`)
      .then((r) => setBlog(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="pt-32 text-center text-slate-500">Loading…</div>;
  if (error || !blog)
    return (
      <div className="pt-32 text-center">
        <div className="text-slate-500 mb-4">Article not found.</div>
        <Link to="/blog" className="text-[#1D4ED8]">← Back to Insights</Link>
      </div>
    );

  const paragraphs = blog.content.split("\n\n");

  return (
    <article className="pt-[72px] bg-white" data-testid="blog-detail-page">
      <SEO
        title={`${blog.title} — Advocate Shubham Makkar`}
        description={blog.excerpt}
        keywords={(blog.keywords || []).join(", ")}
      />

      <div className="max-w-3xl mx-auto px-6 lg:px-10 pt-12 pb-6">
        <Link
          to="/blog"
          className="text-sm text-slate-500 hover:text-[#1D4ED8] inline-flex items-center gap-2 transition-colors"
          data-testid="blog-back-link"
        >
          <ArrowLeft size={14} /> All insights
        </Link>
      </div>

      {blog.cover_image && (
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="aspect-[21/9] overflow-hidden rounded-2xl shadow-xl">
            <img src={blog.cover_image} alt={blog.title} className="w-full h-full object-cover" />
          </div>
        </div>
      )}

      <div className="max-w-3xl mx-auto px-6 lg:px-10 py-12">
        <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#1D4ED8] mb-5">
          {new Date(blog.created_at).toLocaleDateString("en-IN", {
            year: "numeric", month: "long", day: "numeric",
          })}
          {" · "}By {blog.author}
        </div>
        <h1 className="font-serif text-4xl lg:text-5xl text-[#0A2540] tracking-tight leading-[1.1] font-semibold">
          {blog.title}
        </h1>
        <p className="mt-6 text-lg text-slate-600 italic border-l-4 border-[#1D4ED8] pl-5 bg-[#EFF6FF]/30 py-3 pr-4 rounded-r">
          {blog.excerpt}
        </p>
        <div className="mt-10 prose-article">
          {paragraphs.map((p, i) => {
            if (p.startsWith("**") && p.endsWith("**")) {
              return (
                <h3 key={i} className="font-serif text-2xl text-[#0A2540] mt-8 mb-2 font-semibold">
                  {p.replace(/\*\*/g, "")}
                </h3>
              );
            }
            const parts = p.split(/\*\*(.+?)\*\*/g);
            return (
              <p key={i}>
                {parts.map((seg, j) =>
                  j % 2 === 1 ? <strong key={j}>{seg}</strong> : <span key={j}>{seg}</span>
                )}
              </p>
            );
          })}
        </div>

        <div className="mt-14 p-8 bg-gradient-to-br from-[#EFF6FF] to-white border border-blue-100 rounded-2xl shadow-sm">
          <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#1D4ED8] mb-3">
            Need urgent counsel?
          </div>
          <div className="font-serif text-2xl text-[#0A2540] mb-3 font-semibold">
            Speak to Advocate Shubham Makkar directly.
          </div>
          <p className="text-sm text-slate-600 mb-6">
            Every hour matters in corporate cases. Book a consult or message on WhatsApp now.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/book" className="primary-btn" data-testid="blog-book-cta">
              Book Consult
            </Link>
            <a
              href={WHATSAPP_URL()}
              target="_blank"
              rel="noopener noreferrer"
              className="ghost-btn"
              data-testid="blog-whatsapp-cta"
            >
              <Phone size={14} /> WhatsApp
            </a>
          </div>
        </div>
      </div>
    </article>
  );
}
